import { last } from 'lodash';
// @ts-expect-error Could not find a declared module from the generated file, but does not affect functionality
import { parse, SyntaxError } from 'shared/workflows/method_chain_parser';

import { JsonSchema, JsonSchemaTypes } from 'lib/json-schema';
import { assertNever } from 'lib/utils/types';

export const EMPTY_METHOD_CHAIN = {
  type: 'chain',
  links: [],
} as Chain;

export const methodChainCache: { [key: string]: Chain } = {};

export enum MethodChainNodeTypes {
  Chain = 'chain',
  String = 'string',
  Number = 'number',
  Identifier = 'identifier',
  Function = 'function',
  Placeholder = 'placeholder',
}

export type MethodChainNode = IdentifierNode | FunctionNode;

export type FunctionParamType = Chain | StringNode | NumberNode;

export interface Chain {
  type: MethodChainNodeTypes.Chain;
  links: MethodChainNode[];
}

interface StringNode {
  type: MethodChainNodeTypes.String;
  value: string;
}

interface NumberNode {
  type: MethodChainNodeTypes.Number;
  value: number;
}

interface IdentifierNode {
  type: MethodChainNodeTypes.Identifier;
  name: string;
}

interface FunctionNode {
  type: MethodChainNodeTypes.Function;
  name: string;
  params: FunctionParamType[];
}

export const isValid = (methodChain: string): boolean => {
  try {
    parseMethodChain(methodChain);
    return true;
  } catch (SyntaxError) {
    return false;
  }
};

export const parseMethodChain = (methodChain: string): Chain => {
  if (!methodChain || methodChain === '') {
    return EMPTY_METHOD_CHAIN;
  }

  if (!methodChainCache[methodChain]) {
    methodChainCache[methodChain] = parse(methodChain);
  }
  return methodChainCache[methodChain];
};

export const methodChainToString = (methodChain: Chain | StringNode | NumberNode): string => {
  if (
    methodChain.type === MethodChainNodeTypes.String ||
    methodChain.type === MethodChainNodeTypes.Number
  ) {
    return `${methodChain.value}`;
  }

  const regex = new RegExp('^[a-zA-Z0-9_-]+$');
  return methodChain.links
    .map((link, index) => {
      switch (link.type) {
        case MethodChainNodeTypes.Identifier:
          return index === 0
            ? link.name
            : regex.test(link.name)
              ? `.${link.name}`
              : `['${link.name.replace(/'/g, "\\'")}']`;
        case MethodChainNodeTypes.Function:
          return `.${link.name}(${link.params
            .map((param) => {
              switch (param.type) {
                case MethodChainNodeTypes.String:
                  return `'${param.value.replace(/'/g, "\\'")}'`;
                case MethodChainNodeTypes.Number:
                  return `#${param.value}`;
                case MethodChainNodeTypes.Chain:
                  return methodChainToString(param);
              }
            })
            .join(', ')})`;
        default:
          assertNever(link);
      }
    })
    .join('');
};

export const appendedMethodChain = (
  parentChain: Chain,
  appended: string | MethodChainNode,
): Chain => {
  return {
    type: MethodChainNodeTypes.Chain,
    links: [
      ...parentChain.links,
      typeof appended === 'string'
        ? {
            type: MethodChainNodeTypes.Identifier,
            name: appended,
          }
        : appended,
    ],
  };
};

export const parseWithPartialOption = (
  path: string,
): { methodChain: Chain; partial: string; valid: boolean } => {
  if (isValid(path)) {
    const methodChain = parseMethodChain(path);

    return {
      methodChain: {
        type: MethodChainNodeTypes.Chain,
        links: methodChain.links.slice(0, methodChain.links.length - 1),
      },
      partial: last(methodChain.links)?.name || '',
      valid: true,
    };
  }

  try {
    if (path.endsWith('.')) {
      return {
        methodChain: parse(path.slice(0, -1)),
        partial: '',
        valid: true,
      };
    }

    if (path.lastIndexOf('[') > path.lastIndexOf(']')) {
      const partialString = path.slice(path.lastIndexOf('[') + 2);
      const partialValue = partialString.endsWith("'") ? partialString.slice(0, -1) : partialString;
      return {
        methodChain: parse(path.slice(0, path.lastIndexOf('['))),
        partial: partialValue,
        valid: true,
      };
    }
  } catch (SyntaxError) {
    /* empty */
  }

  return {
    methodChain: EMPTY_METHOD_CHAIN,
    partial: path,
    valid: false,
  };
};

// Check if the path can be parsed as legacy method chain format.
// Normative means all the name parts are
// 1. delimited by only dots and
// 2. only contain alphanumeric characters, underscores, and hyphens.
export const pathIsNormative = (path: string): boolean => {
  return path.split('.').every((part) => /^[a-zA-Z0-9_-]+$/.test(part));
};
export { SyntaxError };

export const methodChainNodeToJsonSchema = (node: StringNode | NumberNode): JsonSchema | null => {
  if (node.type === MethodChainNodeTypes.String) {
    return {
      type: JsonSchemaTypes.String,
    };
  } else if (node.type === MethodChainNodeTypes.Number) {
    return {
      type: JsonSchemaTypes.Number,
    };
  }

  return null;
};
